import { useMemo } from "react";
import { TellescopeFormFieldBaseProps } from "../../types";
import { cn, Label } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FieldDescription } from "../../FieldDescription";
import { SortableRankingItem } from "./SortableRankingItem";

export interface RankingFieldProps extends TellescopeFormFieldBaseProps<"ranking"> {}

export const RankingField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  options,
  error,
  touched,
  required,
}: RankingFieldProps) => {
  const { t } = useTranslation("forms");
  const showError = !!(error && touched);

  const order = useMemo(() => (value && value.length > 0 ? value : options?.choices || []), [options?.choices, value]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;

    const oldIndex = order.indexOf(String(active.id));
    const newIndex = order.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    onChange(arrayMove(order, oldIndex, newIndex));
  };

  if (order.length === 0) {
    return (
      <div className={cn("space-y-2", className)}>
        {title && <Label required={required}>{title}</Label>}
        <FieldDescription description={description} htmlDescription={htmlDescription} />
        <p className="text-sm text-muted-foreground">{t("No items to rank")}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className={cn("flex flex-col gap-2", showError && "border border-destructive rounded-md p-2")}>
            {order.map((item) => (
              <SortableRankingItem key={item} item={item} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <p className="text-sm text-muted-foreground">{t("Drag and drop to re-order the above options")}</p>
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
